import { useState, type ReactNode } from 'react';
import { UMIcon } from './UMIcon';
import { UMR } from './referenceTokens';

interface UMToolbarIconButtonProps {
  icon: string;
  label: string;
  /** Tooltip when it differs from the accessible label (prod: aria "Actions", title "Voir les actions"). */
  title?: string;
  onClick?: () => void;
}

export function UMToolbarIconButton({ icon, label, title, onClick }: UMToolbarIconButtonProps) {
  const [state, setState] = useState<'idle' | 'hover' | 'active'>('idle');
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      onClick={onClick}
      onMouseEnter={() => setState('hover')}
      onMouseLeave={() => setState('idle')}
      onMouseDown={() => setState('active')}
      onMouseUp={() => setState('hover')}
      style={{
        width: UMR.size.iconButton,
        height: UMR.size.iconButton,
        flexShrink: 0,
        border: 'none',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        background: state === 'active' ? UMR.active : state === 'hover' ? UMR.hover : 'transparent',
        fontFamily: UMR.iconFont,
        fontSize: 20,
        color: UMR.black,
      }}
    >
      <UMIcon name={icon} size={20} color={UMR.black} style={{ height: 24 }} />
    </button>
  );
}

interface UMToolbarInputProps {
  placeholder: string;
  value: string;
  trailingIcon: string;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  onClick?: () => void;
  ariaLabel: string;
}

/** Fluid v5 `fluid-input` as rendered in production (300×32, 1px grey-400, square). */
export function UMToolbarInput({ placeholder, value, trailingIcon, readOnly, onChange, onClick, ariaLabel }: UMToolbarInputProps) {
  const [hover, setHover] = useState(false);
  return (
    <div style={{ position: 'relative', width: UMR.size.inputWidth, height: UMR.size.inputHeight, flexShrink: 0 }}>
      <input
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="um-toolbar-input"
        style={{
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          border: `1px solid ${UMR.borderInput}`,
          borderRadius: 0,
          padding: '1px 16px',
          fontSize: 11,
          fontFamily: 'system-ui',
          color: UMR.black,
          background: hover ? UMR.hover : UMR.white,
          cursor: readOnly ? 'pointer' : 'text',
          outline: 'none',
        }}
      />
      <span style={{ position: 'absolute', right: 5, top: 6, pointerEvents: 'none' }}>
        <UMIcon name={trailingIcon} family="icons" size={20} color={UMR.black} />
      </span>
    </div>
  );
}

export interface UMMapToolbarProps {
  /** Selected folder label; when empty the production placeholder "Aucune sélection" is shown. */
  selectionLabel?: string;
  selections?: string[];
  onSelectionChange?: (value: string) => void;
  onCreateFolder?: () => void;
  /** With a folder selected, production replaces "create folder" by these two buttons. */
  onCenterSelection?: () => void;
  onSelectionActions?: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  onLocate?: () => void;
  /** Extra items appended after the locate button (keep it sparse — not in production). */
  extra?: ReactNode;
}

/** Top-left map toolbar: selection dropdown, create folder, search, locate. */
export function UMMapToolbar({
  selectionLabel = '',
  selections = [],
  onSelectionChange,
  onCreateFolder,
  onCenterSelection,
  onSelectionActions,
  search,
  onSearchChange,
  searchPlaceholder = 'Rechercher',
  onLocate,
  extra,
}: UMMapToolbarProps) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: UMR.size.toolbarPadding,
        padding: UMR.size.toolbarPadding,
        background: UMR.white,
        borderBottom: `1px solid ${UMR.border}`,
        borderRight: `1px solid ${UMR.border}`,
        fontFamily: UMR.font,
        position: 'relative',
      }}
    >
      <style>{'.um-toolbar-input::placeholder{color:var(--nj-core-color-grey-600,#718492)}.um-toolbar-input:focus{border:2px solid var(--nj-core-color-blue-600,#007acd)!important}'}</style>
      <div style={{ position: 'relative' }}>
        <UMToolbarInput
          ariaLabel="Sélection"
          placeholder="Aucune sélection"
          value={selectionLabel}
          trailingIcon="keyboard_arrow_down"
          readOnly
          onClick={() => setOpen((v) => !v)}
        />
        {open && (
          <ul
            role="listbox"
            style={{
              position: 'absolute', top: UMR.size.inputHeight, left: 0, width: UMR.size.inputWidth,
              margin: 0, padding: 0, listStyle: 'none', background: UMR.white,
              border: `1px solid ${UMR.border}`, zIndex: 40, fontSize: 14,
            }}
          >
            {selections.length === 0 && <li style={{ padding: '8px 16px', color: UMR.textMuted }}>Aucun dossier</li>}
            {selections.map((s) => (
              <li
                key={s}
                role="option"
                aria-selected={s === selectionLabel}
                onClick={() => { onSelectionChange?.(s); setOpen(false); }}
                style={{ padding: '8px 16px', cursor: 'pointer' }}
              >
                {s}
              </li>
            ))}
          </ul>
        )}
      </div>
      {selectionLabel ? (
        <div style={{ display: 'flex' }}>
          <UMToolbarIconButton icon="center_focus_strong" label="Centrer sur ma sélection" onClick={onCenterSelection} />
          <UMToolbarIconButton icon="more_vert" label="Actions" title="Voir les actions" onClick={onSelectionActions} />
        </div>
      ) : (
        <UMToolbarIconButton icon="create_new_folder" label="Créer une sélection" onClick={onCreateFolder} />
      )}
      <UMToolbarInput
        ariaLabel={searchPlaceholder}
        placeholder={searchPlaceholder}
        value={search}
        trailingIcon="search"
        onChange={onSearchChange}
      />
      <UMToolbarIconButton icon="my_location" label="Me localiser" onClick={onLocate} />
      {extra}
    </div>
  );
}
